import { useSyncExternalStore } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatBytes } from '../media/opfs';
import { useSession } from '../app/session';
import { db } from '../db/db';
import { getStatus, retryNow, subscribe } from '../sync/engine';
import { Sheet } from './Sheet';
import { EMPTY } from '@kilroy/shared';
import './syncsheet.css';

/* Sync detail. Everything the engine knows, shown plainly.
 *
 * The queue is listed item by item on purpose. "3 queued" is a number; a list
 * of three named things you recognise is a promise that they still exist. */

const KIND_LABEL: Record<string, string> = {
  evidence: 'Observation',
  correction: 'Correction',
  status: 'Status change',
  ranking: 'Ranking',
  rectified: 'Rectified order',
  comment: 'Comment',
  media: 'File',
};

export function SyncSheet({ onClose, total }: { onClose: () => void; total: number }) {
  const status = useSyncExternalStore(subscribe, getStatus);
  const { storage } = useSession();
  const queue = useLiveQuery(() => db.outbox.orderBy('seq').toArray(), [], []);

  return (
    <Sheet title="Sync" onClose={onClose}>
      {status.narration && <p className="sync__narration">{status.narration}</p>}

      <div className="sync__stats">
        <div className="stat">
          <div className="stat__value">{status.queued}</div>
          <div className="stat__label">Records queued</div>
        </div>
        <div className="stat">
          <div className="stat__value">{status.mediaQueued}</div>
          <div className="stat__label">Files queued</div>
        </div>
        <div className="stat">
          <div className="stat__value">{formatBytes(storage.usageBytes)}</div>
          <div className="stat__label">On device</div>
        </div>
        <div className="stat">
          <div className="stat__value">{storage.persisted ? 'Yes' : 'No'}</div>
          <div className="stat__label">Storage protected</div>
        </div>
      </div>

      {queue.length === 0 ? (
        <div className="empty">
          <div className="empty__t">{EMPTY.queue}</div>
          <div className="empty__h">Everything on this phone has reached the team.</div>
        </div>
      ) : (
        <ul className="queue" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {queue.map((row) => (
            <li key={row.seq} className="queue__row">
              <span className="queue__seq">#{row.seq}</span>
              <span className="grow">
                <div className="queue__kind">{KIND_LABEL[row.kind] ?? row.kind}</div>
                <div className="queue__meta">
                  {row.attempts === 0
                    ? 'waiting for signal'
                    : `${row.attempts} attempt${row.attempts === 1 ? '' : 's'}`}
                </div>
              </span>
              {row.last_error && <span className="queue__err">{row.last_error}</span>}
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="btn btn--quiet btn--lg"
        onClick={() => void retryNow()}
        disabled={total === 0}
      >
        Retry now
      </button>

      <p
        className="mono"
        style={{ color: 'var(--ink-soft)', marginTop: 'var(--s-4)', lineHeight: 1.5 }}
      >
        Nothing leaves this phone until the server confirms it. Files are kept
        on-device until then.
      </p>
    </Sheet>
  );
}
